import React, { createContext, useContext, useState, useEffect, useRef, useMemo } from "react"
import { useTheme } from '~/contexts/ThemeContext'
import usePlpHook from '~/hooks/usePlpHook'
import usePlpProductsHook from '~/hooks/usePlpProductsHook'
import useFilterHook from '~/utils/use-filtering'
import useAnalytics from '~/utils/analytics'
import PlpInit from '~/initializers/plp-init'

import { off } from "~/styles/plp/plp.module.scss"

const PlpContext = createContext()

export function PlpProvider({ children, collData }) {
  const collection = collData
  const { ana_plp } = useAnalytics()
  const { 
    setBlockHeader, bread, setBread, setGender, 
    analytics, setAnalytics, pageProps, setPageProps,
    setQuick, setFreeze, collPanel, 
    setCollPanel, setCollGender, locate, setLocate
  } = useTheme()
  
  const { upd_filters, parse_filters, upd_params } = useFilterHook()

  const plpHook = usePlpHook({ collection })
  const { checkFilterParams, checkFeatParams, afterFilterParams, anaProducts } = plpHook

  const { title, handle } = collection
  const { grp_default, shw_grpnav, shw_singpl } = collection.rules || {}

  const allProducts = useMemo(() => collData.products, [collData])
  const allFilters = useMemo(() => collData.allFilters, [collData])
  const showGrpNav = useMemo(() => (shw_grpnav && shw_grpnav.length > 0) || false, [shw_grpnav])
  const singPlTiles = useMemo(() => shw_singpl || false, [shw_singpl])
  const initialLoadComplete = useRef(false)
  const panelRef = useRef(null)

  const [prodsBuilt, setProdsBuilt] = useState(false)
  const [didLoad, setDidLoad] = useState(false)
  const [currGroup, setCurrGroup] = useState(grp_default)
  const [currGrpId, setCurrGrpId] = useState(false)
  const [flats, setFlats] = useState(collection.gender === 'w' ? 'model' : 'flat')
  const [scale, setScale] = useState(1)
  const [mores, setMores] = useState([])

  const [products, setProducts] = useState(collData.initProducts)
  const [ungroups, setUngroups] = useState(collData.initUngroups)
  const [filters, setFilters] = useState([])
  const [currSort, setCurrSort] = useState(false)
  const [allColors, setAllColors] = useState(false)
  const [featIds, setFeatIds] = useState(false)
  const [searching, setSearching] = useState(false)
  const [searchVals, setSearchVals] = useState([])

  const { buildProducts } = usePlpProductsHook({ collection, setProducts, setUngroups, plpHook })
  
  const panelClose = () => {
    const panel_el = panelRef && panelRef.current
    if (panel_el) {
      panel_el.setAttribute('aria-hidden', 'true')
      panel_el.classList.add(off)
    }
    setFreeze(false)
    setTimeout(() => {
      if (panel_el) panel_el.classList.remove(off)
      setCollPanel(false)
    }, 301)
  }
  
  const getGroupName = (id) => {
    if (!id) return false
    let group_name = id ? collection.groups.find(x => x.mid === id) : false
    if (group_name) group_name = group_name.data.name
    return group_name
  }

  const onFilter = (adding) => {
    if (adding && adding !== 'clear') adding.type = adding.type === 'groupstyle' ? 'style' : adding.type
    if (adding === 'clear') {
      handleClear()
      return
    }
    setFilters(filters => upd_filters(adding, filters, getGroupName(currGrpId)))
  }

  const onGroup = (id) => {
    const is_on = id === false || currGrpId === id
    setAllColors(false)
    setCurrGrpId(is_on ? false : id)
  }

  const handleClear = () => {
    setCurrGrpId(false)
    setFilters(filters => {
      return filters.filter(x => x.type === 'locations')
    })
  }

  const handleScale = () => {
    const new_scale = scale === 2 ? 1 : scale + 1
    setScale(new_scale)
    window._conv_q = window._conv_q || []
    window._conv_q.push(["triggerConversion", "100444001"])
  }

  const handleQuick = (handle, variant=false) => {
    let product = allProducts.find(x => x.handle === handle)
    if (product) {
      let quick_prods = allProducts.filter(x => x.hasOwnProperty('pl_pima') && x.pl_pima === product.pl_pima)
      let obj = {selected: product.shop_id, products: quick_prods}
      if (variant) obj['variant'] = variant

      window._conv_q = window._conv_q || []
      window._conv_q.push(["triggerConversion", "100458750"])
      setQuick(obj)

      if (variant) return
      // Quick view general
    }
  }

  const checkBread = () => {
    if (bread && bread.click) {
      const newtop = bread.scroll
      setBread(curr => {curr.click = false; return curr})
    }
    if (!bread || bread.handle !== handle) {
      setBread({handle: handle, title: title, scroll: 0, click: false})
    }
  }

  const handleMore = (id) => {
    setMores(items => [...items, id])
  }

  const onPanel = (name) => {
    if (collPanel === name || name === false) {
      panelClose()
      return
    }
    setCollPanel(name)
    setFreeze(true)
    setTimeout(()=>{
      if (panelRef.current) panelRef.current.setAttribute('aria-hidden', 'false')
    }, 50)
  }

  const { fillCollection } = PlpInit()

  useEffect(() => {
    const is_wsingletest = collection.handle === 'womens-tees' && !!sessionStorage.getItem('wsingle')
    if (is_wsingletest && !collection?.rules?.shw_singpl) {
      collection.rules.shw_singpl = true
      const newfill = fillCollection(collection)
      collection.initProducts = newfill.initProducts
      setProducts(newfill.initProducts)
    }

    // checkInv()
    if (!didLoad) {
      setDidLoad(true)
      setGender(collection.gender)
      setCollGender(collection.gender)
      setBlockHeader(false)
      checkBread()
      
      setAnalytics({...analytics, list: `/collections/${collection.handle}`})
      setPageProps({...pageProps, pageType: `collection`})
    }

    return () => {
      setCollGender(null)
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!(didLoad && locate)) return

    const no_stores = locate?.currStores?.length < 1
    const nofilts = !searching && filters.length < 1 && !currGrpId && !currSort && no_stores

    if (!prodsBuilt && nofilts) {
      setProdsBuilt(true)
      return
    }

    const props = {
      products: products, 
      filters, locate, 
      currGrpId, currGroup, 
      currSort, featIds, 
      searching, searchVals
    }
    buildProducts(props)
    setProdsBuilt(true)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [didLoad, filters, locate, currSort, currGrpId, currGroup, featIds, searching])

  useEffect(() => {
    if (!prodsBuilt) return
    
    checkFilterParams({setCurrGrpId, onFilter, locate, setLocate})
    checkFeatParams({ setFeatIds })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prodsBuilt])

  useEffect(() => {
    if (!didLoad) return

    afterFilterParams({filters, currSort, currGrpId, featIds, getGroupName, upd_params})

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, currSort, currGrpId, featIds])

  useEffect(() => {
    if (products.length < 1) return

    const prodsarr = anaProducts(products)
    ana_plp(prodsarr, handle, true)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products])


  return (
    <PlpContext.Provider value={{
      collection, plpHook, 
      products, setProducts,
      allProducts, allFilters, 
      currGroup, setCurrGroup,
      currGrpId, onGroup, 
      showGrpNav, singPlTiles,
      flats, setFlats,
      scale, setScale, handleScale, 
      mores, handleMore,
      handleQuick, onFilter, 
      filters, setFilters, 
      currSort, setCurrSort,
      allColors, setAllColors,
      onPanel, panelRef, panelClose,
      ungroups, featIds,
      searching, setSearching,
      searchVals, setSearchVals
    }}>
      {children}
    </PlpContext.Provider>
  );
}

export function usePlp() {
  const context = useContext(PlpContext)
  if (context === undefined) {
    throw new Error('usePlp must be used within an PlpProvider')
  }
  return context
}

